import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuestion } from "../../../../redux/Reducers/QuestionReducer";
import QuestionHeader from "../component/QuestionHeader";
import ListenAudio from "../../../../components/Audio/ListenAudio";
import { DetButton } from "../../../../components/Form/Button/FormButton";
import useForm from "../../../../hooks/useForm";
import URLS from "../../../../API/Axios/URLS";
import GenericQuestionModal from "../Modal/GenericQuestionModal/GenricQuestionModal";

// --- Sub-components for each phase ---

// Phase 1: Listen & Complete
function ListenComplete({ data, audio, scenario, onNext }) {
    const [answers, setAnswers] = useState({});
    const [isChecked, setIsChecked] = useState(false);

    const handleInputChange = (index, value) => {
        if (isChecked) return; // Disable editing after check
        setAnswers(prev => ({ ...prev, [index]: value }));
    };

    const isAllFilled = data?.options?.every((opt, idx) => answers[idx] && answers[idx].trim().length > 0);

    const handleCheck = () => {
        setIsChecked(true);
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 flex flex-col gap-4">
                <h3 className="font-bold text-lg text-blue-900">Task 1: Listen & Complete</h3>
                <p className="text-blue-800">Listen into the scenario and complete the missing information in the sentences below.</p>

                {audio && (
                    <div className="w-full bg-white p-3 rounded-md shadow-sm">
                        <ListenAudio audio={audio} autoPlay />
                    </div>
                )}

            </div>

            <div className="flex flex-col gap-4">
                {data?.options?.map((option, index) => {
                    const parts = option.completeSentence.split(/\[(.*?)\]/g);
                    // answer is usually in the option.answer field or extracted from brackets
                    // distinct answer field is provided in JSON: option.answer
                    const correctAnswer = option.answer;
                    const userAnswer = answers[index] || "";
                    const isCorrect = isChecked && userAnswer.toLowerCase().trim() === correctAnswer.toLowerCase().trim();

                    return (
                        <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                            <p className="font-semibold text-gray-700 mb-2">{option.title}</p>
                            <div className="text-lg leading-relaxed flex flex-wrap items-baseline gap-1">
                                {parts.map((part, i) => {
                                    if (i % 2 === 1) {
                                        return (
                                            <span key={i} className="relative inline-block">
                                                <input
                                                    type="text"
                                                    disabled={isChecked}
                                                    className={`
                                                        border-b-2 outline-none px-1 mx-1 font-medium min-w-[150px]
                                                        ${!isChecked
                                                            ? "border-gray-300 focus:border-blue-500 bg-gray-50 text-blue-600"
                                                            : isCorrect
                                                                ? "border-green-500 bg-green-50 text-green-700"
                                                                : "border-red-500 bg-red-50 text-red-700"
                                                        }
                                                    `}
                                                    placeholder="Type here..."
                                                    value={userAnswer}
                                                    onChange={(e) => handleInputChange(index, e.target.value)}
                                                />
                                                {isChecked && !isCorrect && (
                                                    <span className="text-sm text-green-600 font-bold ml-2">({correctAnswer})</span>
                                                )}
                                            </span>
                                        );
                                    }
                                    return <span key={i}>{part}</span>;
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-end mt-4 gap-4">
                {!isChecked ? (
                    <DetButton onClick={handleCheck} disabled={!isAllFilled} className="w-40 h-[42px]">
                        Submit
                    </DetButton>
                ) : (
                    <DetButton onClick={onNext} className="w-40 h-[42px]">
                        Next Section
                    </DetButton>
                )}
            </div>
        </div>
    );
}

// Phase 2: Listen & Respond
function ListenRespond({ data, onNext }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [stepStatus, setStepStatus] = useState({}); // { [index]: 'pending' | 'checked' } 
    const items = data?.items || [];

    const handleChoiceSelect = (choiceId) => {
        if (stepStatus[currentIndex] === 'checked') return;
        setUserAnswers(prev => ({ ...prev, [currentIndex]: choiceId }));
    };

    const handleSubmit = () => {
        setStepStatus(prev => ({ ...prev, [currentIndex]: 'checked' }));
    };

    const handleNext = () => {
        if (currentIndex < items.length - 1) {
            setCurrentIndex(currentIndex + 1);
        } else {
            onNext();
        }
    };

    const currentItem = items[currentIndex];
    const isChecksDone = stepStatus[currentIndex] === 'checked';
    const isLastItem = currentIndex === items.length - 1;

    return (
        <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto h-full justify-center">
            <div className="bg-purple-50 p-4 rounded-md border border-purple-100 mb-4">
                <h3 className="font-bold text-lg mb-2 text-purple-900">Task 2: Listen & Respond</h3>
                <p className="text-purple-800">You will participate in a conversation. Listen to the audio and choose the best response, or listen to the interlocutor.</p>
            </div>

            <div className="flex flex-col items-center justify-center min-h-[300px] gap-6">
                {currentItem?.type === 2 && (
                    <div className="flex flex-col items-center gap-4 w-full animate-fadeIn">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🎧</span>
                        </div>
                        <h4 className="text-xl font-medium text-gray-800">Interlocutor is speaking...</h4>
                        <ListenAudio audio={currentItem.audioUrl} autoPlay className="w-full max-w-md" />

                        <DetButton className="mt-6 w-40 h-[42px]" onClick={handleNext}>
                            {isLastItem ? "Finish" : "Continue"}
                        </DetButton>
                    </div>
                )}

                {currentItem?.type === 1 && (
                    <div className="flex flex-col gap-3 w-full animate-fadeIn">
                        <h4 className="text-lg font-medium text-gray-700 mb-2">Choose the best response:</h4>
                        {currentItem.choices.map((choice, idx) => {
                            const isSelected = userAnswers[currentIndex] === choice.id;
                            const isCorrect = choice.correct === "1";

                            let borderColor = "border-gray-200 hover:border-blue-300 hover:bg-gray-50";
                            let bgColor = "";
                            let iconColor = "border-gray-300";

                            if (isChecksDone) {
                                if (isCorrect) {
                                    borderColor = "border-green-500 bg-green-50";
                                    iconColor = "border-green-500 bg-green-500";
                                } else if (isSelected && !isCorrect) {
                                    borderColor = "border-red-500 bg-red-50";
                                    iconColor = "border-red-500 bg-red-500";
                                } else {
                                    // dim others
                                    borderColor = "border-gray-100 opacity-50";
                                }
                            } else if (isSelected) {
                                borderColor = "border-blue-500 bg-blue-50 shadow-md";
                                iconColor = "border-blue-500";
                            }


                            return (
                                <div
                                    key={idx}
                                    onClick={() => handleChoiceSelect(choice.id)}
                                    className={`
                                        p-4 rounded-lg border-2 cursor-pointer transition-all
                                        ${borderColor} ${bgColor}
                                    `}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`
                                            w-6 h-6 rounded-full border-2 flex items-center justify-center
                                            ${iconColor}
                                        `}>
                                            {isSelected && !isChecksDone && <div className="w-3 h-3 rounded-full bg-blue-500" />}
                                            {isChecksDone && isCorrect && <span className="text-white text-xs">✓</span>}
                                            {isChecksDone && isSelected && !isCorrect && <span className="text-white text-xs">✕</span>}
                                        </div>
                                        <p className="text-gray-800 text-lg">{choice.options[0]}</p>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="mt-6 self-end">
                            {!isChecksDone ? (
                                <DetButton
                                    className="w-40 h-[42px]"
                                    onClick={handleSubmit}
                                    disabled={!userAnswers[currentIndex]}
                                >
                                    Submit
                                </DetButton>
                            ) : (
                                <DetButton className="w-40 h-[42px]" onClick={handleNext}>
                                    {isLastItem ? "Finish" : "Next"}
                                </DetButton>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="w-full bg-gray-200 h-2 rounded-full mt-8">
                <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentIndex + 1) / items.length) * 100}%` }}
                />
            </div>
        </div>
    );
}

// Phase 3: Task Completed
function TaskCompletion({ onNextQuestion, nextQuestion }) {
    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto h-[400px] justify-center items-center">
            <div className="bg-green-100 p-8 rounded-full mb-4">
                <span className="text-6xl">🎉</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-800">Task Completed!</h2>
            <p className="text-lg text-gray-600">You have successfully finished the interactive listening practice.</p>

            {nextQuestion && (
                <DetButton
                    onClick={onNextQuestion}
                    className="w-auto px-6 h-[42px] mt-4"
                >
                    Next Interactive Listening
                </DetButton>
            )}
        </div>
    );
}


export default function InteractiveListening({ className, ...props }) {
    const { id } = useParams()
    const { question, questionActions } = useQuestion()
    const [currentPhase, setCurrentPhase] = useState(1); // 1, 2, or 3
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        questionActions.loadQuestions(id);
    }, [id]);

    const [form, error, loading, setFormData, actions] = useForm({
        questionId: id,
        answer: "",
    })

    const handlePhase1Next = () => setCurrentPhase(2);
    const handlePhase2Next = () => setCurrentPhase(3);
    const handlePhase3Finish = (summaryText) => {
        // Submit logic
        // Mocking submission for now or using standard action
        actions.post(URLS.genericQuestionView, {
            // Adapting payload might be needed, but sticking to standard for now
            answer: summaryText
        }).then(() => {
            setShowModal(true);
        });
    };

    if (!question) {
        return <div className="p-8 text-center">Loading Question...</div>;
    }

    // Normalize data: check items first, then fallback to root
    const qData = question.items || question;
    const listenCompleteData = qData.listenComplete;
    const listenRespondData = qData.listenRespond;

    // Scenario might be on root (if from DB) or in items
    const scenarioText = question.scenario || qData.scenario;
    // Audio might be on root (if using audioUrl for it) or in items as 'scenarioAudio'
    const scenarioAudio = qData.scenarioAudio || question.audioUrl;

    const nextQuestion = question.metaData?.next;

    const handleNextQuestion = () => {
        if (nextQuestion) {
            // Assuming route format /practice/interactive-listening/:id
            // Using standard route construction if needed, or just navigating to ID if route is same
            navigate(`/practice/interactive-listening/${nextQuestion.id}`);
            setCurrentPhase(1); // Reset phase for new question
        }
    };

    if (!listenCompleteData) {
        return <div className="p-8 text-center">Loading Question Data...</div>;
    }

    return (
        <div className={`rounded-lg shadow-lg p-6 bg-white min-h-[600px] ${className}`}>
            <QuestionHeader {...question} />

            <div className="py-8">
                {currentPhase === 1 && (
                    <ListenComplete
                        data={listenCompleteData}
                        scenario={scenarioText}
                        audio={scenarioAudio}
                        onNext={handlePhase1Next}
                    />
                )}

                {currentPhase === 2 && (
                    <ListenRespond
                        data={listenRespondData}
                        onNext={handlePhase2Next}
                    />
                )}

                {currentPhase === 3 && (
                    <TaskCompletion
                        nextQuestion={nextQuestion}
                        onNextQuestion={handleNextQuestion}
                    />
                )}
            </div>

            <GenericQuestionModal
                result={{}} // Result data
                showModal={showModal}
                setShowModal={setShowModal}
            />

            {/* Phase indicator */}
            <div className="flex justify-center gap-2 mt-8 opacity-50">
                <div className={`h-2 w-2 rounded-full ${currentPhase === 1 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`h-2 w-2 rounded-full ${currentPhase === 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                <div className={`h-2 w-2 rounded-full ${currentPhase === 3 ? 'bg-blue-600' : 'bg-gray-300'}`} />
            </div>
        </div>
    );
}
